import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Badge, 
  Button, 
  Dropdown,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Alert
} from 'react-bootstrap';
import { 
  fetchStreams, 
  deleteStream, 
  fetchStreamStats,
  regenerateStreamKey
} from '../../../store/actions/StreamActions';
import swal from 'sweetalert';

const StreamList = ({ 
  streams, 
  loading, 
  error, 
  deleting,
  regenerating,
  fetchStreams, 
  deleteStream,
  fetchStreamStats,
  regenerateStreamKey
}) => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [streamToDelete, setStreamToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchStreams();
    fetchStreamStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStreams();
      fetchStreamStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchStreams, fetchStreamStats]);

  const handleDeleteClick = (stream) => {
    setStreamToDelete(stream);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (streamToDelete) {
      deleteStream(streamToDelete.id, (err) => {
        if (!err) {
          swal("Deleted!", "Stream has been deleted.", "success");
        } else {
          swal("Error!", "Failed to delete stream.", "error");
        }
        setShowDeleteModal(false);
        setStreamToDelete(null);
      });
    }
  };

  const getStatusBadge = (status, isLive) => {
    if (isLive) {
      return <Badge bg="success">Live</Badge>;
    }
    
    switch (status) {
      case 'broadcasting':
        return <Badge bg="success">Broadcasting</Badge>;
      case 'active':
        return <Badge bg="primary">Active</Badge>;
      case 'inactive':
        return <Badge bg="secondary">Inactive</Badge>;
      case 'disconnected':
        return <Badge bg="warning">Disconnected</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      swal("Copied!", `${label} copied to clipboard.`, "success", {
        timer: 1500,
        buttons: false
      });
    });
  };

  const handleRegenerateKey = (streamId) => {
    swal({
      title: "Regenerate Stream Key?",
      text: "This will generate a new stream key. The old key will no longer work!",
      icon: "warning",
      buttons: ["Cancel", "Regenerate"],
      dangerMode: true,
    }).then((willRegenerate) => {
      if (willRegenerate) {
        regenerateStreamKey(streamId, (err) => {
          if (!err) {
            swal("Success!", "Stream key has been regenerated.", "success");
            fetchStreams();
          } else {
            swal("Error!", "Failed to regenerate stream key.", "error");
          }
        });
      }
    });
  };

  const filteredStreams = streams.filter(stream => {
    const matchesSearch = stream.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         stream.stream_key.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || stream.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading && streams.length === 0) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading streams...</p>
      </div>
    );
  }

  return (
    <>
      <div className="form-head d-flex mb-3 mb-md-4 align-items-start">
        <div className="me-auto d-none d-lg-block">
          <h3 className="text-black font-w600">Streams</h3>
          <p className="mb-0">Manage your RTMP streams and HLS outputs</p>
        </div>
        <div>
          <Button 
            variant="primary" 
            onClick={() => navigate('/create-stream')}
            className="d-flex align-items-center"
          >
            <i className="fa fa-plus me-2" aria-hidden="true"></i>
            Create Stream
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col lg={6}>
          <Form.Control
            type="text"
            placeholder="Search streams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col lg={3}>
          <Form.Select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="broadcasting">Broadcasting</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="disconnected">Disconnected</option>
          </Form.Select>
        </Col>
        <Col lg={3} className="text-end">
          <Button 
            variant="outline-primary" 
            onClick={() => { fetchStreams(); fetchStreamStats(); }}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-1`} aria-hidden="true"></i>
            Refresh
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Stream Name</th>
                <th>Status</th>
                <th>Stream Key</th>
                <th>Viewers</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStreams.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4">
                    {searchTerm || statusFilter !== 'all' 
                      ? 'No streams match your filters' 
                      : 'No streams found. Create your first stream!'}
                  </td>
                </tr>
              ) : (
                filteredStreams.map((stream) => (
                  <tr key={stream.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="stream-icon me-3">
                          <i className="fas fa-video text-primary" aria-hidden="true"></i>
                        </div>
                        <div>
                          <h6 className="mb-0">{stream.name}</h6>
                          <small className="text-muted">
                            {stream.description || 'No description'}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {getStatusBadge(stream.status, stream.is_live)}
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <code className="bg-light px-2 py-1 rounded text-break" style={{maxWidth: '200px', fontSize: '0.85em'}}>
                          {stream.stream_key}
                        </code>
                        <div className="ms-2 d-flex">
                          <Button 
                            variant="link" 
                            size="sm"
                            onClick={() => copyToClipboard(stream.stream_key, 'Stream key')}
                            className="p-0 me-2"
                            title="Copy stream key"
                          >
                            <i className="fa fa-copy" aria-hidden="true"></i>
                          </Button>
                          <Button 
                            variant="link" 
                            size="sm"
                            onClick={() => handleRegenerateKey(stream.id)}
                            className="p-0 text-warning"
                            title="Regenerate key"
                            disabled={regenerating}
                          >
                            <i className={`fas fa-sync-alt ${regenerating ? 'fa-spin' : ''}`} aria-hidden="true"></i>
                          </Button>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={stream.is_live ? 'text-success fw-bold' : ''}>
                        {stream.viewer_count || 0}
                      </span>
                    </td>
                    <td>
                      {new Date(stream.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="light" size="sm">
                          Actions
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item 
                            onClick={() => navigate(`/streams/${stream.id}`)}
                          >
                            <i className="fa fa-eye me-2" aria-hidden="true"></i>
                            View Details
                          </Dropdown.Item>
                          <Dropdown.Item 
                            onClick={() => copyToClipboard(stream.rtmp_url, 'RTMP URL')}
                          >
                            <i className="fa fa-copy me-2" aria-hidden="true"></i>
                            Copy RTMP URL
                          </Dropdown.Item>
                          <Dropdown.Item 
                            onClick={() => copyToClipboard(stream.hls_url, 'HLS URL')}
                          >
                            <i className="fa fa-copy me-2" aria-hidden="true"></i>
                            Copy HLS URL
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item 
                            className="text-danger"
                            onClick={() => handleDeleteClick(stream)}
                          >
                            <i className="fa fa-trash me-2" aria-hidden="true"></i>
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the stream "{streamToDelete?.name}"?
          <br />
          <small className="text-muted">
            This action cannot be undone. The stream key will be permanently removed.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Stream'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  streams: state.streams.streams,
  loading: state.streams.loading,
  error: state.streams.error,
  deleting: state.streams.deleting,
  regenerating: state.streams.regenerating
});

const mapDispatchToProps = {
  fetchStreams,
  deleteStream,
  fetchStreamStats,
  regenerateStreamKey
};

export default connect(mapStateToProps, mapDispatchToProps)(StreamList);
